'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

// ============================================================================
// CART CONTEXT
// ============================================================================

export interface CartItem {
  variantId: string
  productId: string
  slug: string
  title: string
  size: string | null
  price: number
  image: string | null
  quantity: number
  maxStock: number
}

interface CartContextType {
  items: CartItem[]
  count: number
  subtotal: number
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void
  removeItem: (variantId: string) => void
  updateQuantity: (variantId: string, quantity: number) => void
  clear: () => void
}

const STORAGE_KEY = 'shop_cart'

const CartContext = createContext<CartContextType | undefined>(undefined)

function clampQuantity(quantity: number, maxStock: number): number {
  if (quantity < 1) return 1
  if (maxStock > 0 && quantity > maxStock) return maxStock
  return quantity
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([])
  const [loaded, setLoaded] = useState(false)

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        setItems(JSON.parse(stored) as CartItem[])
      }
    } catch {
      setItems([])
    } finally {
      setLoaded(true)
    }
  }, [])

  // Persist cart whenever it changes (after the initial load)
  useEffect(() => {
    if (!loaded) return
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch {
      // Ignore storage errors (e.g. private mode)
    }
  }, [items, loaded])

  const addItem = (item: Omit<CartItem, 'quantity'>, quantity = 1) => {
    setItems((current) => {
      const existing = current.find((i) => i.variantId === item.variantId)
      if (existing) {
        return current.map((i) =>
          i.variantId === item.variantId
            ? {
                ...i,
                maxStock: item.maxStock,
                price: item.price,
                quantity: clampQuantity(i.quantity + quantity, item.maxStock),
              }
            : i
        )
      }
      return [...current, { ...item, quantity: clampQuantity(quantity, item.maxStock) }]
    })
  }

  const removeItem = (variantId: string) => {
    setItems((current) => current.filter((i) => i.variantId !== variantId))
  }

  const updateQuantity = (variantId: string, quantity: number) => {
    setItems((current) =>
      current.map((i) =>
        i.variantId === variantId
          ? { ...i, quantity: clampQuantity(quantity, i.maxStock) }
          : i
      )
    )
  }

  const clear = () => setItems([])

  const count = items.reduce((sum, i) => sum + i.quantity, 0)
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0)

  return (
    <CartContext.Provider
      value={{ items, count, subtotal, addItem, removeItem, updateQuantity, clear }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
